import React, { useState, useEffect } from 'react';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { 
  MagnifyingGlassIcon,
  XMarkIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

interface GooglePlace {
  place_id: string;
  name: string;
  formatted_address: string;
  rating?: number;
  user_ratings_total?: number;
  business_status?: string;
  types: string[];
}

interface GooglePlaceSearchResponse {
  places: GooglePlace[];
}

interface GoogleBusinessSetupModalProps {
  isOpen: boolean;
  onClose: () => void;
  isRelinking?: boolean;
}

export const GoogleBusinessSetupModal: React.FC<GoogleBusinessSetupModalProps> = ({
  isOpen,
  onClose,
  isRelinking = false,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchingPlaces, setSearchingPlaces] = useState(false);
  const [places, setPlaces] = useState<GooglePlaceSearchResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [verificationStatus, setVerificationStatus] = useState<any>(null);
  const [pendingPlace, setPendingPlace] = useState<GooglePlace | null>(null);
  const [showWarningDialog, setShowWarningDialog] = useState(false);
  
  const queryClient = useQueryClient();

  // Fetch current verification status and place when modal opens (always, to check if switching)
  const { data: currentVerification, isLoading: isLoadingVerification } = useQuery({
    queryKey: ['verification-status'],
    queryFn: async () => {
      const response = await api.analytics.getVerificationStatus();
      return response.data;
    },
    enabled: isOpen, // Always fetch when modal is open
  });

  const { data: currentPlace, isLoading: isLoadingPlace } = useQuery({
    queryKey: ['google-place-current'],
    queryFn: async () => {
      const response = await api.analytics.getGooglePlace();
      return response.data;
    },
    enabled: isOpen, // Always fetch when modal is open
  });

  // Search Google Places mutation
  const searchGooglePlacesMutation = useMutation({
    mutationFn: async () => {
      const response = await api.analytics.searchGooglePlace({
        query: searchQuery,
        location: '', // Let users search globally
      });
      return response.data as GooglePlaceSearchResponse;
    },
    onSuccess: (data) => {
      setPlaces(data);
      setError(null);
    },
    onError: (err: any) => {
      setError(err?.response?.data?.detail || 'Failed to search Google Places');
    },
  });

  // Link Google Place mutation
  const linkGooglePlaceMutation = useMutation({
    mutationFn: async (placeId: string) => {
      const response = await api.analytics.linkGooglePlace(placeId);
      return response.data;
    },
    onSuccess: (data) => {
      // Check if verification data is present
      if (data.verification) {
        setVerificationStatus(data.verification);
        
        if (data.verification.status === 'verified') {
          setSuccess(isRelinking ? 'Google Business Profile updated and verified successfully!' : 'Google Business Profile linked and verified successfully!');
        } else {
          setSuccess(isRelinking ? 'Google Business Profile updated successfully!' : 'Google Business Profile linked successfully!');
        }
      } else {
        setSuccess(isRelinking ? 'Google Business Profile updated successfully!' : 'Google Business Profile linked successfully!');
      }
      
      queryClient.invalidateQueries({ queryKey: ['analytics', 'dashboard', 'kpis'] }); // To update business name
      queryClient.invalidateQueries({ queryKey: ['verification-status'] });
      queryClient.invalidateQueries({ queryKey: ['rating-settings'] }); // Invalidate rating settings to sync
      // Don't invalidate analytics-overview - campaign stats are not affected by place_id change
      
      // Only auto-close if verified, otherwise show verification status
      if (data.verification?.status === 'verified') {
        setTimeout(() => {
          onClose();
          setSuccess(null);
          setSearchQuery('');
          setPlaces(null);
          setVerificationStatus(null);
        }, 3000);
      }
    },
    onError: (err: any) => {
      setError(err?.response?.data?.detail || 'Failed to link Google Business Profile');
    },
  });

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      setError('Please enter a business name to search');
      return;
    }
    
    setSearchingPlaces(true);
    setError(null);
    setPlaces(null);
    
    try {
      await searchGooglePlacesMutation.mutateAsync();
    } finally {
      setSearchingPlaces(false);
    }
  };

  const handlePlaceSelect = async (place: GooglePlace) => {
    // Wait for queries to finish if they're still loading
    if (isLoadingVerification || isLoadingPlace) {
      // Wait a bit for queries to complete
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    // Check if there's an existing business with verification that would be reset
    const currentPlaceId = currentPlace?.place_data?.place_id;
    const verificationStatus = currentVerification?.status;
    
    // Show warning if:
    // 1. There's an existing linked business
    // 2. There's a verification status (pending or verified)
    // 3. The new place_id is different from the current one
    if (currentPlace?.linked && currentPlaceId && verificationStatus && 
        place.place_id !== currentPlaceId && 
        (verificationStatus === 'pending' || verificationStatus === 'verified')) {
      setPendingPlace(place);
      setShowWarningDialog(true);
      return;
    }
    
    // Proceed with linking
    proceedWithLinking(place.place_id);
  };

  const proceedWithLinking = async (placeId: string) => {
    try {
      await linkGooglePlaceMutation.mutateAsync(placeId);
      setPendingPlace(null);
      setShowWarningDialog(false);
    } catch (error) {
      // Error is handled by the mutation
      setPendingPlace(null);
      setShowWarningDialog(false);
    }
  };

  const handleConfirmSwitch = () => {
    if (pendingPlace) {
      proceedWithLinking(pendingPlace.place_id);
    }
  };

  const handleCancelSwitch = () => {
    setPendingPlace(null);
    setShowWarningDialog(false);
  };

  const handleClose = () => {
    onClose();
    setSearchQuery('');
    setPlaces(null);
    setError(null);
    setSuccess(null);
    setVerificationStatus(null);
    setPendingPlace(null);
    setShowWarningDialog(false);
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Warning Dialog */}
      {showWarningDialog && pendingPlace && (
        <div className="fixed inset-0 z-[60] overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4 text-center sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={handleCancelSwitch} />
            
            <div className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
              <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-yellow-100 sm:mx-0 sm:h-10 sm:w-10">
                    <ExclamationTriangleIcon className="h-6 w-6 text-yellow-600" aria-hidden="true" />
                  </div>
                  <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                    <h3 className="text-base font-semibold leading-6 text-gray-900">
                      Reset Verification?
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        You are switching to a different business. This will reset your verification status 
                        {currentVerification?.status === 'verified' ? ' (currently verified)' : ' (currently pending)'}.
                        You'll need to verify the new business after linking.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                <button
                  type="button"
                  className="inline-flex w-full justify-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 sm:ml-3 sm:w-auto"
                  onClick={handleConfirmSwitch}
                >
                  Continue & Reset
                </button>
                <button
                  type="button"
                  className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                  onClick={handleCancelSwitch}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Modal */}
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex min-h-screen items-center justify-center p-4 text-center sm:p-0">
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={handleClose} />
        
        <div className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
          <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                    <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  </div>
                </div>
                <div className="ml-3">
                  <h3 className="text-lg font-medium text-gray-900">
                    {isRelinking ? 'Update Google Business Profile' : 'Connect Google Business Profile'}
                  </h3>
                  <p className="text-sm text-gray-500">
                    {isRelinking ? 'Search for a different business to link' : 'Link your business to track Google reviews and ratings'}
                  </p>
                </div>
              </div>
              <button
                onClick={handleClose}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            {success && (
              <div className="mb-4 rounded-md bg-green-50 p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <CheckCircleIcon className="h-5 w-5 text-green-400" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-green-800">{success}</p>
                  </div>
                </div>
              </div>
            )}

            {error && (
              <div className="mb-4 rounded-md bg-red-50 p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-red-800">{error}</p>
                  </div>
                </div>
              </div>
            )}

            {verificationStatus && (
              <div className={`mb-4 rounded-md p-4 ${
                verificationStatus.status === 'verified' 
                  ? 'bg-green-50' 
                  : verificationStatus.status === 'pending'
                  ? 'bg-yellow-50'
                  : 'bg-gray-50'
              }`}>
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    {verificationStatus.status === 'verified' ? (
                      <CheckCircleIcon className="h-5 w-5 text-green-400" />
                    ) : (
                      <ExclamationTriangleIcon className={`h-5 w-5 ${
                        verificationStatus.status === 'pending' ? 'text-yellow-400' : 'text-gray-400'
                      }`} />
                    )}
                  </div>
                  <div className="ml-3 flex-1">
                    <h4 className={`text-sm font-medium ${
                      verificationStatus.status === 'verified'
                        ? 'text-green-800'
                        : verificationStatus.status === 'pending'
                        ? 'text-yellow-800'
                        : 'text-gray-800'
                    }`}>
                      Verification Status: {verificationStatus.status === 'verified' ? 'Verified ✓' : verificationStatus.status === 'pending' ? 'Pending' : 'Not Verified'}
                    </h4>
                    <div className="mt-2 text-sm space-y-1">
                      {verificationStatus.phone_verified !== undefined && (
                        <div className={`flex items-center ${
                          verificationStatus.phone_verified ? 'text-green-700' : 'text-gray-600'
                        }`}>
                          <span className="mr-2">{verificationStatus.phone_verified ? '✓' : '○'}</span>
                          <span>Phone: {verificationStatus.phone_verified ? 'Verified' : verificationStatus.phone_match === false ? 'No match' : 'Not available'}</span>
                        </div>
                      )}
                      {verificationStatus.email_verified !== undefined && (
                        <div className={`flex items-center ${
                          verificationStatus.email_verified ? 'text-green-700' : 'text-gray-600'
                        }`}>
                          <span className="mr-2">{verificationStatus.email_verified ? '✓' : '○'}</span>
                          <span>Email Domain: {verificationStatus.email_verified ? 'Verified' : verificationStatus.email_match === false ? 'No match' : 'Not available'}</span>
                        </div>
                      )}
                    </div>
                    {verificationStatus.status !== 'verified' && (
                      <p className="mt-2 text-xs text-gray-600">
                        Verification helps ensure you own or manage this business. You can continue using the app even if verification is pending.
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label htmlFor="search-query" className="block text-sm font-medium text-gray-700 mb-2">
                  Search for your business
                </label>
                <div className="flex space-x-2">
                  <input
                    type="text"
                    id="search-query"
                    placeholder="Enter your business name..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <button
                    onClick={handleSearch}
                    disabled={searchingPlaces || !searchQuery.trim()}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                  >
                    {searchingPlaces ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                    ) : (
                      <MagnifyingGlassIcon className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>

              {searchingPlaces && (
                <div className="flex justify-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" />
                </div>
              )}

              {places && places.places.length > 0 && (
                <div>
                  <div className="text-sm text-gray-600 font-medium mb-2">
                    Found {places.places.length} result{places.places.length !== 1 ? 's' : ''} - Select your business:
                  </div>
                  <div className="space-y-2 max-h-60 overflow-y-auto border border-gray-200 rounded-md">
                    {places.places.map((place) => (
                      <button
                        key={place.place_id}
                        onClick={() => handlePlaceSelect(place)}
                        disabled={linkGooglePlaceMutation.isPending}
                        className="w-full text-left p-3 border-b border-gray-100 hover:bg-gray-50 hover:border-blue-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <div className="font-medium text-gray-900">{place.name}</div>
                        <div className="text-sm text-gray-600">{place.formatted_address}</div>
                        {place.rating && (
                          <div className="text-sm text-gray-500 flex items-center">
                            <span className="text-yellow-400">⭐</span>
                            <span className="ml-1">{place.rating} ({place.user_ratings_total} reviews)</span>
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {places && places.places.length === 0 && !searchingPlaces && (
                <div className="text-sm text-gray-500 py-4 text-center">
                  No results found. Try searching with a different business name or address.
                </div>
              )}
            </div>
          </div>

          <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
            <button
              type="button"
              onClick={handleClose}
              className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};
